import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { listSpots, type Spot } from '../lib/api-client';

export function SpotsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['spots.list', includeDeleted],
    queryFn: () => listSpots(includeDeleted),
  });

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">景點管理（F-3）</h1>
        <div className="page-head__actions" style={{ alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            顯示已刪除
          </label>
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
          <Button variant="primary" onClick={() => navigate('/spots/new')}>
            + 新增景點
          </Button>
        </div>
      </div>

      <p className="page-intro">
        景點 CRUD + 多語系 + 版本化（AC-F3-1~5）；點 spot_code 可編輯，版本切換 UI 後續加入。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && <div className="error-banner">{(error as Error).message}</div>}
      {data && (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>spot_code</th>
                <th>名稱（zh-TW）</th>
                <th>座標</th>
                <th>觸發半徑</th>
                <th>狀態</th>
                <th>建立時間</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-cell">
                    尚無景點資料
                  </td>
                </tr>
              )}
              {data.map((s: Spot) => (
                <tr key={s.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    <Link to={`/spots/${s.id}`}>{s.spot_code}</Link>
                  </td>
                  <td>{s.name_i18n['zh-TW'] ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {Number(s.latitude).toFixed(4)}, {Number(s.longitude).toFixed(4)}
                  </td>
                  <td>{s.trigger_radius_m} m</td>
                  <td>
                    {s.deleted_at ? (
                      <Pill tone="danger">已刪除</Pill>
                    ) : s.is_published ? (
                      <Pill tone="success">已發布</Pill>
                    ) : (
                      <Pill tone="warning">草稿</Pill>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                    {new Date(s.created_at).toLocaleString('zh-TW')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
